import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatError, absoluteUrl } from "@/api/client";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { useWorkspaceBrand } from "@/context/WorkspaceBrandContext";
import { SortableList } from "@/components/SortableList";
import { RichTextEditor } from "@/components/RichTextEditor";
import { DatePicker } from "@/components/DatePicker";
import { printAgreement, feeTotals, pctOf } from "@/lib/agreementPdf";
import { currency } from "@/lib/printDoc";
import { ArrowLeft, FloppyDisk, FilePdf, Copy, Plus, Trash, X, BookmarkSimple, CurrencyInr, CaretUp, CaretDown, Receipt, UploadSimple, Eye, PaperPlaneTilt, CheckCircle, PencilLine } from "@phosphor-icons/react";

const tmpId = () => `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export default function WorkOrderEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { workspace } = useWorkspaceBrand();
  const [a, setA] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    api.get(`/agreements/${id}`).then((r) => { setA(r.data); setDirty(false); }).catch((e) => toast.error(formatError(e)));
  }, [id]);

  const patch = (p) => { setA((x) => ({ ...x, ...p })); setDirty(true); };
  const save = async (silent) => {
    setSaving(true);
    try { const { data } = await api.put(`/agreements/${id}`, a); setA(data); setDirty(false); if (!silent) toast.success("Work order saved"); return data; }
    catch (e) { toast.error(formatError(e)); return null; }
    finally { setSaving(false); }
  };
  const pdf = async () => { const d = dirty ? await save(true) : a; if (d) printAgreement(d, workspace); };
  const saveTemplate = async () => {
    try { await api.put("/agreements/template", a); toast.success("Saved as the starting template for new work orders"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const openDuplicate = async () => { if (dirty && !(await save(true))) return; setDupOpen(true); };
  const invoiceMilestone = async (idx) => {
    if (dirty && !(await save(true))) return;
    try {
      const { data } = await api.post(`/agreements/${id}/milestones/${idx}/invoice`);
      setA((x) => ({ ...x, fees: { ...x.fees, milestones: x.fees.milestones.map((m, i) => (i === idx ? { ...m, invoices: data.invoices } : m)) } }));
      toast.success(`${data.invoices.length} Proforma invoice${data.invoices.length > 1 ? "s" : ""} created: ${data.invoices.map((v) => v.invoice_number).join(", ")}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  if (!a) return <div className="p-8 text-neutral-500">Loading…</div>;
  const setSection = (sid, p) => patch({ sections: a.sections.map((s) => (s.id === sid ? { ...s, ...p } : s)) });

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto fade-in" data-testid="wo-editor">
      <button onClick={() => nav("/business")} data-testid="wo-editor-back" className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 mb-4"><ArrowLeft size={14} /> Back to Work Orders</button>
      <div className="sticky top-0 z-20 bg-neutral-50/90 backdrop-blur-md -mx-2 px-2 py-3 mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200">
        <div className="min-w-0">
          <div className="font-mono text-xs text-neutral-500" data-testid="wo-editor-number">{a.wo_number}</div>
          <h1 className="font-display text-xl md:text-2xl font-bold text-neutral-900 truncate">{a.title || "Untitled work order"}</h1>
          <div className="text-[11px] mt-0.5" data-testid="wo-editor-save-state">{dirty ? <span className="text-amber-600">Unsaved changes</span> : <span className="text-neutral-400">All changes saved</span>}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={saveTemplate} data-testid="wo-save-template-btn" className="dos-btn dos-btn-secondary text-xs" title="Use this content as the starting point for new work orders"><BookmarkSimple size={14} /> Save as Template</button>
          <button onClick={openDuplicate} data-testid="wo-duplicate-btn" className="dos-btn dos-btn-secondary text-xs"><Copy size={14} /> Duplicate</button>
          <button onClick={pdf} data-testid="wo-pdf-btn" className="dos-btn dos-btn-secondary text-xs"><FilePdf size={14} /> Generate PDF</button>
          <button onClick={() => save()} disabled={saving || !dirty} data-testid="wo-save-btn" className="dos-btn dos-btn-primary text-xs"><FloppyDisk size={14} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <DetailsCard a={a} patch={patch} />
      <SignOffCard a={a} onUpdated={(d) => setA((x) => ({ ...x, status: d.status, sent_at: d.sent_at, signed_at: d.signed_at, signed_file_id: d.signed_file_id, signed_filename: d.signed_filename }))} />

      <div className="dos-card p-5 mb-4" data-testid="wo-intro-card">
        <input value={a.intro_heading} onChange={(e) => patch({ intro_heading: e.target.value })} placeholder="Introduction heading" data-testid="wo-intro-heading" className="dos-input font-semibold mb-2" />
        <RichTextEditor value={a.intro_html} onChange={(v) => patch({ intro_html: v })} testId="wo-intro-body" minHeight={80} />
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Clauses — drag the handle to reorder</div>
        <button onClick={() => setCollapsed((c) => !c)} data-testid="wo-collapse-toggle" className="dos-btn dos-btn-secondary text-xs">{collapsed ? <><CaretDown size={12} /> Expand all</> : <><CaretUp size={12} /> Collapse all to reorder</>}</button>
      </div>
      <SortableList className="space-y-3 mb-3" items={a.sections} getId={(s) => s.id} onReorder={(next) => patch({ sections: next })} renderItem={(s, idx, handle) => (
        <SectionCard s={s} idx={idx} handle={handle} collapsed={collapsed} onInvoice={invoiceMilestone} canInvoice={!!(a.company_id && a.client_id)} onChange={(p) => setSection(s.id, p)} fees={a.fees} onFees={(f) => patch({ fees: f })}
          onRemove={() => patch({ sections: a.sections.filter((x) => x.id !== s.id) })} />
      )} />
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => patch({ sections: [...a.sections, { id: tmpId(), heading: "New Clause", body_html: "<p></p>", kind: "text" }] })} data-testid="wo-add-section-btn" className="dos-btn dos-btn-secondary text-xs"><Plus size={14} /> Add Clause</button>
        {!a.sections.some((s) => s.kind === "fees") && (
          <button onClick={() => patch({ sections: [...a.sections, { id: tmpId(), heading: "Commercial Terms & Payment Milestones", body_html: "", kind: "fees" }] })} data-testid="wo-add-fees-section-btn" className="dos-btn dos-btn-secondary text-xs"><CurrencyInr size={14} /> Add Fees & Milestones Clause</button>
        )}
      </div>

      <SignatoriesCard a={a} patch={patch} />
      {dupOpen && <DuplicateWorkOrderModal source={a} onClose={() => setDupOpen(false)} onDone={(d) => { setDupOpen(false); nav(`/business/work-orders/${d.id}`); }} />}
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs text-neutral-500 mb-1 block">{label}</label>{children}</div>;
}

function DetailsCard({ a, patch }) {
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);
  const sel = (k, opts, ph) => (
    <select value={a[k] || ""} onChange={(e) => patch({ [k]: e.target.value })} data-testid={`wo-field-${k}`} className="dos-input">
      <option value="">{ph}</option>
      {opts.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
    </select>
  );
  const inp = (k, ph) => <input value={a[k] || ""} onChange={(e) => patch({ [k]: e.target.value })} placeholder={ph} data-testid={`wo-field-${k}`} className="dos-input" />;
  return (
    <div className="dos-card p-5 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="wo-details-card">
      <div className="md:col-span-2"><Field label="Agreement title">{inp("title", "e.g. Architectural & Interior Design Agreement")}</Field></div>
      <div className="md:col-span-2"><Field label="Project">{inp("project_name", "Project name and location")}</Field></div>
      <Field label="Date"><DatePicker value={a.date} onChange={(v) => patch({ date: v })} testId="wo-field-date" /></Field>
      <Field label="Work order number">{inp("wo_number", "Auto-generated")}</Field>
      <Field label="Design consultant">{inp("consultant_name", "e.g. Gray Owl Architects, Pune")}</Field>
      <Field label="Client">{inp("client_name", "Client name(s)")}</Field>
      <Field label="Bill from company (for milestone invoices)">{sel("company_id", companies, "Select company")}</Field>
      <Field label="Bill to client (for milestone invoices)">{sel("client_id", clients, "Select client")}</Field>
    </div>
  );
}

function SectionCard({ s, idx, handle, collapsed, onChange, onRemove, fees, onFees, onInvoice, canInvoice }) {
  const confirm = useConfirm();
  const remove = async () => {
    if (await confirm({ title: `Delete clause "${s.heading || idx + 1}"?`, message: "Its text will be removed from this work order.", confirmLabel: "Delete", tone: "danger" })) onRemove();
  };
  return (
    <div className={`dos-card ${collapsed ? "p-2" : "p-4"}`} data-testid={`wo-section-${idx}`}>
      <div className={`flex items-center gap-2 ${collapsed ? "" : "mb-2"}`}>
        {handle}
        <span className="font-mono text-xs text-neutral-400 w-5">{idx + 1}.</span>
        <input value={s.heading} onChange={(e) => onChange({ heading: e.target.value })} data-testid={`wo-section-heading-${idx}`} className="dos-input font-semibold" style={{ flex: 1, width: "auto" }} placeholder="Clause heading" />
        <button onClick={remove} data-testid={`wo-section-delete-${idx}`} title="Delete clause" className="p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
      </div>
      {!collapsed && s.kind === "fees" && <FeesEditor fees={fees} onChange={onFees} onInvoice={onInvoice} canInvoice={canInvoice} />}
      {!collapsed && s.kind === "fees" && <div className="text-[11px] text-neutral-500 mt-3 mb-1">Payment terms (shown after the tables)</div>}
      {!collapsed && <RichTextEditor value={s.body_html} onChange={(v) => onChange({ body_html: v })} testId={`wo-section-body-${idx}`} />}
    </div>
  );
}

function FeesEditor({ fees, onChange, onInvoice, canInvoice }) {
  const [busyIdx, setBusyIdx] = useState(null);
  const confirm = useConfirm();
  const f = { lines: [], milestones: [], gst_percent: 18, milestone_base: 0, note: "", milestone_title: "", ...fees };
  const t = feeTotals(f);
  const base = parseFloat(f.milestone_base) || 0;
  const setList = (k, i, p) => onChange({ ...f, [k]: f[k].map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const rm = (k, i) => onChange({ ...f, [k]: f[k].filter((_, j) => j !== i) });
  const num = (v) => (v === "" ? "" : parseFloat(v) || 0);
  return (
    <div className="bg-neutral-50 rounded-md p-3 space-y-4" data-testid="wo-fees-editor">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">Fee Structure</div>
        {f.lines.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_10rem_auto] gap-2 mb-1.5 items-center">
            <input value={l.label} onChange={(e) => setList("lines", i, { label: e.target.value })} data-testid={`wo-fee-label-${i}`} className="dos-input py-1 text-sm" placeholder="e.g. Consultancy Fee for Plot 1" />
            <input type="number" value={l.amount} onChange={(e) => setList("lines", i, { amount: num(e.target.value) })} data-testid={`wo-fee-amount-${i}`} className="dos-input py-1 text-sm font-mono" />
            <button onClick={() => rm("lines", i)} className="p-1 text-neutral-400 hover:text-red-500"><X size={14} /></button>
          </div>
        ))}
        <button onClick={() => onChange({ ...f, lines: [...f.lines, { label: "", amount: 0 }] })} data-testid="wo-fee-add" className="text-xs text-neutral-600 hover:text-neutral-900">+ Add fee line</button>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-2 text-sm">
          <label className="flex items-center gap-2 text-xs text-neutral-500 whitespace-nowrap">GST %<input type="number" value={f.gst_percent} onChange={(e) => onChange({ ...f, gst_percent: num(e.target.value) })} data-testid="wo-fee-gst" className="dos-input py-1 text-sm" style={{ width: 72 }} /></label>
          <span className="text-neutral-500">Subtotal <b className="font-mono text-neutral-900" data-testid="wo-fee-subtotal">{currency(t.subtotal)}</b></span>
          <span className="text-neutral-500">GST <b className="font-mono text-neutral-900">{currency(Math.round(t.gst))}</b></span>
          <span className="text-neutral-500">Total <b className="font-mono text-neutral-900" data-testid="wo-fee-grand">{currency(Math.round(t.grand))}</b></span>
        </div>
        <input value={f.note} onChange={(e) => onChange({ ...f, note: e.target.value })} placeholder="Note (optional)" data-testid="wo-fee-note" className="dos-input py-1 text-sm mt-2" />
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <input value={f.milestone_title} onChange={(e) => onChange({ ...f, milestone_title: e.target.value })} data-testid="wo-ms-title" className="dos-input py-1 text-sm font-semibold" style={{ flex: "1 1 240px", width: "auto" }} placeholder="Milestone Payment Schedule" />
          <label className="flex items-center gap-2 text-xs text-neutral-500 whitespace-nowrap">% based on<input type="number" value={f.milestone_base} onChange={(e) => onChange({ ...f, milestone_base: num(e.target.value) })} data-testid="wo-ms-base" className="dos-input py-1 text-sm font-mono" style={{ width: 130 }} /></label>
        </div>
        <SortableList items={f.milestones.map((m, i) => ({ ...m, _k: i }))} getId={(m) => `ms-${m._k}`} onReorder={(next) => onChange({ ...f, milestones: next.map(({ _k, ...m }) => m) })} renderItem={(m, i, handle) => (
          <div className="grid grid-cols-[auto_1.5rem_1fr_3.5rem_9rem_8.5rem_auto] items-center gap-2 mb-1.5 bg-neutral-50" data-testid={`wo-ms-row-${i}`}>
            {handle}
            <span className="font-mono text-[11px] text-neutral-400">{String(i + 1).padStart(2, "0")}</span>
            <input value={m.trigger} onChange={(e) => setList("milestones", i, { trigger: e.target.value })} data-testid={`wo-ms-trigger-${i}`} className="dos-input py-1 text-sm" placeholder="Stage / trigger" />
            <span className="font-mono text-xs text-neutral-500 text-right" data-testid={`wo-ms-pct-${i}`}>{pctOf(m.amount, base).toFixed(1)}%</span>
            <input type="number" value={m.amount} onChange={(e) => setList("milestones", i, { amount: num(e.target.value) })} data-testid={`wo-ms-amount-${i}`} className="dos-input py-1 text-sm font-mono" />
            {m.invoices?.length ? (
              <div className="text-[11px] leading-tight text-emerald-700 font-medium truncate" data-testid={`wo-ms-invoiced-${i}`} title={m.invoices.map((v) => `${v.invoice_number} · ${v.line_label}`).join("\n")}>
                <CheckCircle size={11} weight="fill" className="inline -mt-0.5" /> {m.invoices[0].invoice_number.split("-").pop()}{m.invoices.length > 1 ? ` +${m.invoices.length - 1}` : ""}
                <div className="text-[10px] text-neutral-500 font-normal">{m.invoices.length} Proforma{m.invoices.length > 1 ? "s" : ""}</div>
              </div>
            ) : (
              <button type="button" disabled={busyIdx === i} data-testid={`wo-ms-invoice-btn-${i}`}
                onClick={async () => {
                  if (!canInvoice) { toast.error("Pick the billing company and client in the work order details first"); return; }
                  const n = f.lines.length || 1;
                  if (!(await confirm({ title: `Create ${n} Proforma invoice${n > 1 ? "s" : ""}?`, message: `Milestone ${String(i + 1).padStart(2, "0")} — ${currency(m.amount)} + GST${n > 1 ? `, one for each fee line (${f.lines.map((l) => l.label).join(", ")})` : ""}.`, confirmLabel: "Create" }))) return;
                  setBusyIdx(i); await onInvoice(i); setBusyIdx(null);
                }}
                className="dos-btn dos-btn-secondary text-[11px] px-2 py-1"><Receipt size={12} /> {busyIdx === i ? "Creating…" : "Invoice"}</button>
            )}
            <button onClick={() => rm("milestones", i)} className="p-1 text-neutral-400 hover:text-red-500"><X size={14} /></button>
          </div>
        )} />
        <div className="flex flex-wrap items-center justify-between gap-2 mt-1">
          <button onClick={() => onChange({ ...f, milestones: [...f.milestones, { trigger: "", amount: 0 }] })} data-testid="wo-ms-add" className="text-xs text-neutral-600 hover:text-neutral-900">+ Add milestone</button>
          <div className="text-sm text-neutral-500">Milestones total <b className="font-mono text-neutral-900" data-testid="wo-ms-total">{currency(t.milestoneTotal)}</b> ({pctOf(t.milestoneTotal, base).toFixed(1)}%)
            {base > 0 && Math.round(t.milestoneTotal) !== Math.round(base) && <span className="ml-2 text-amber-600 text-xs" data-testid="wo-ms-mismatch">doesn't match {currency(base)}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SignatoriesCard({ a, patch }) {
  const list = a.signatories || [];
  const set = (i, p) => patch({ signatories: list.map((s, j) => (j === i ? { ...s, ...p } : s)) });
  return (
    <div className="dos-card p-5 mb-10" data-testid="wo-signatories-card">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Signatures & Acceptance</div>
      <textarea value={a.acceptance_text} onChange={(e) => patch({ acceptance_text: e.target.value })} rows={2} data-testid="wo-acceptance-text" className="dos-input text-sm mb-3" />
      {list.map((s, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-2" data-testid={`wo-signatory-${i}`}>
          <input value={s.party} onChange={(e) => set(i, { party: e.target.value })} placeholder="For …" className="dos-input py-1 text-sm" />
          <input value={s.role} onChange={(e) => set(i, { role: e.target.value })} placeholder="(Role)" className="dos-input py-1 text-sm" />
          <input value={s.name} onChange={(e) => set(i, { name: e.target.value })} placeholder="Name" data-testid={`wo-signatory-name-${i}`} className="dos-input py-1 text-sm" />
          <button onClick={() => patch({ signatories: list.filter((_, j) => j !== i) })} className="p-1 text-neutral-400 hover:text-red-500"><Trash size={14} /></button>
        </div>
      ))}
      <button onClick={() => patch({ signatories: [...list, { party: "For the Client", role: "", name: "" }] })} data-testid="wo-signatory-add" className="text-xs text-neutral-600 hover:text-neutral-900">+ Add signatory</button>
    </div>
  );
}

export const WO_STATUS = {
  draft: { label: "Draft", cls: "bg-neutral-100 text-neutral-700", Icon: PencilLine },
  sent: { label: "Sent", cls: "bg-blue-50 text-blue-700", Icon: PaperPlaneTilt },
  signed: { label: "Signed", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle },
};

function SignOffCard({ a, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const status = a.status || "draft";
  const setStatus = async (s) => {
    try { const { data } = await api.patch(`/agreements/${a.id}/status`, { status: s }); onUpdated(data); toast.success(`Marked as ${WO_STATUS[s].label}`); }
    catch (e) { toast.error(formatError(e)); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/agreements/${a.id}/signed-copy`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdated(data); toast.success("Signed copy uploaded — marked as Signed");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };
  const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
  return (
    <div className="dos-card p-4 mb-4 flex flex-wrap items-center gap-4 justify-between" data-testid="wo-signoff-card">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Client sign-off</span>
        <div className="flex rounded-md border border-neutral-200 overflow-hidden">
          {Object.entries(WO_STATUS).map(([k, { label, Icon }]) => (
            <button key={k} type="button" onClick={() => k !== status && setStatus(k)} data-testid={`wo-status-${k}`}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${status === k ? "bg-neutral-900 text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}>
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-neutral-400" data-testid="wo-status-dates">
          {status !== "draft" && a.sent_at && `Sent ${when(a.sent_at)}`}{status === "signed" && a.sent_at && a.signed_at && " · "}{a.signed_at && status === "signed" && `Signed ${when(a.signed_at)}`}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {a.signed_file_id && (
          <a href={absoluteUrl(`/api/uploads/pdf/${a.signed_file_id}`)} target="_blank" rel="noopener noreferrer" data-testid="wo-signed-view" className="dos-btn dos-btn-secondary text-xs" title={a.signed_filename}><Eye size={14} /> View signed copy</a>
        )}
        <label className={`dos-btn dos-btn-secondary text-xs cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          <UploadSimple size={14} /> {uploading ? "Uploading…" : a.signed_file_id ? "Replace signed copy" : "Upload signed copy"}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" data-testid="wo-signed-upload" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      </div>
    </div>
  );
}

export function DuplicateWorkOrderModal({ source, onClose, onDone }) {
  const [title, setTitle] = useState(`${source.title} (Copy)`);
  const [project, setProject] = useState(source.project_name || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/agreements/${source.id}/duplicate`, { title, project_name: project }); toast.success("Copy created — edit it for the new project"); onDone(data); }
    catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-start justify-center p-4" onClick={onClose} data-testid="wo-duplicate-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg border border-neutral-200 w-full max-w-md mt-24 p-5 space-y-3 fade-in">
        <div className="flex items-center justify-between"><div className="font-display font-semibold">Duplicate work order</div><button type="button" onClick={onClose}><X size={16} /></button></div>
        <p className="text-xs text-neutral-500">All clauses, fees and signatories are copied. A new work order number is assigned.</p>
        <Field label="Name for the copy"><input required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="wo-duplicate-title" className="dos-input" /></Field>
        <Field label="Project"><input value={project} onChange={(e) => setProject(e.target.value)} data-testid="wo-duplicate-project" className="dos-input" /></Field>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="dos-btn dos-btn-secondary">Cancel</button>
          <button type="submit" disabled={busy} data-testid="wo-duplicate-submit" className="dos-btn dos-btn-primary">{busy ? "Copying…" : "Create Copy"}</button>
        </div>
      </form>
    </div>
  );
}
